'use client';

import { loginWithFirebaseIdToken } from '@/hooks/use-firebase-id-token-login';
import { readArmsFailDetail } from '@/utils/arms/common';
import { reportLoginFail, reportLoginSuccess } from '@/utils/arms/login-events';
import { trackCustom, ETrackerEvents } from '@/utils/tracker';

import type { IFirebaseOAuthRedirectTracking } from '@/lib/auth/firebase-oauth-session';

export interface ICompleteFirebaseSocialLoginParams {
    idToken: string;
    tracking: IFirebaseOAuthRedirectTracking;
    /** 业务登录失败时的 toast；默认 "Login failed" */
    onErrorToast?: (message: string) => void;
}

export type TCompleteFirebaseSocialLoginResult =
    | { success: true; isGuestUpgrade: boolean }
    | { success: false; reason: 'device_id_missing' | 'login_failed' };

/**
 * Firebase 三方登录拿到 idToken 后的统一业务登录与 LOGIN_RESULT 埋点。
 */
export async function completeFirebaseSocialLogin(
    params: ICompleteFirebaseSocialLoginParams
): Promise<TCompleteFirebaseSocialLoginResult> {
    const { idToken, tracking, onErrorToast } = params;
    const toast = onErrorToast ?? (() => {});

    try {
        const { isGuestUpgrade } = await loginWithFirebaseIdToken(idToken);
        const login_type = isGuestUpgrade ? 'new_bind' : 'switch_account';
        trackCustom(ETrackerEvents.LOGIN_RESULT, {
            trigger_scene: tracking.trigger_scene,
            login_method: tracking.login_method,
            result_status: 'success',
            login_type,
        });
        reportLoginSuccess({
            login_method: tracking.login_method,
            trigger_scene: tracking.trigger_scene,
            login_type,
        });
        return { success: true, isGuestUpgrade };
    } catch (e: unknown) {
        trackCustom(ETrackerEvents.LOGIN_RESULT, {
            trigger_scene: tracking.trigger_scene,
            login_method: tracking.login_method,
            result_status: 'fail',
        });
        if ((e as Error)?.message === 'device_id_missing') {
            reportLoginFail({
                login_method: tracking.login_method,
                trigger_scene: tracking.trigger_scene,
                fail_reason: 'device_id_missing',
            });
            toast('Device not ready, please try again later.');
            return { success: false, reason: 'device_id_missing' };
        }
        reportLoginFail({
            login_method: tracking.login_method,
            trigger_scene: tracking.trigger_scene,
            fail_reason: 'auth_login_api_failed',
            fail_detail: readArmsFailDetail(e),
        });
        toast('Login failed');
        return { success: false, reason: 'login_failed' };
    }
}
